/**
 * Icon set semantico per gli stati Zanshin.
 *
 * Convenzione comune:
 *   - Tutti i componenti accettano `width`/`height` ma le icone emoji li ignorano
 *     (font-size fisso 16px come da spec UX). Solo gli SVG scudi rispettano i dimens.
 *   - Tutti hanno aria-label appropriato per accessibilita'.
 *
 * Mapping stati Zanshin → icona (vedi ZanshinPairCard.getStateMeta):
 *   nessuna op           → NoOpIcon            ⏸️
 *   HEDGE                → HedgeIcon           ⚡
 *   BROKER_SHIELD F1     → ShieldF1Icon        SVG scudo blu galattico 残心
 *   BROKER_SHIELD F2     → ShieldF2Icon        SVG scudo viola galattico 残心
 *   RUNNER_BROKER        → RunnerBrokerIcon    🚀
 *   RUNNER_PROP          → RunnerPropIcon      📈
 *   RUNNER_ATTIVO        → MomentumIcon        🔥
 *   (FORCE_CLOSE event)  → ForceCloseIcon      ⛔ (riservato a uso futuro)
 */

interface IconProps {
  width?: number | string
  height?: number | string
  className?: string
}

// ─── Emoji wrappers ─────────────────────────────────────────────────────────
const Emoji = ({ char, label, className }: { char: string; label: string; className?: string }) => (
  <span
    role="img"
    aria-label={label}
    className={className}
    style={{ fontSize: 16, lineHeight: 1, display: 'inline-block' }}
  >
    {char}
  </span>
)

export const NoOpIcon         = (p: IconProps) => <Emoji char="⏸️" label="nessuna operazione" className={p.className} />
export const HedgeIcon        = (p: IconProps) => <Emoji char="⚡"  label="hedge attivo"      className={p.className} />
export const RunnerBrokerIcon = (p: IconProps) => <Emoji char="🚀" label="runner broker"     className={p.className} />
export const RunnerPropIcon   = (p: IconProps) => <Emoji char="📈" label="runner prop"       className={p.className} />
export const MomentumIcon     = (p: IconProps) => <Emoji char="🔥" label="momentum attivo"   className={p.className} />
export const ForceCloseIcon   = (p: IconProps) => <Emoji char="⛔" label="chiusura forzata"  className={p.className} />

// ─── SVG scudi custom (kanji 残心 = "Zanshin") ──────────────────────────────
export const ShieldF1Icon = ({ width = 20, height = 23, className }: IconProps) => (
  <svg width={width} height={height} viewBox="0 0 52 60" fill="none"
       className={className} role="img" aria-label="protezione F1 attiva">
    <defs>
      <radialGradient id="zsh-s1" cx="38%" cy="28%" r="70%">
        <stop offset="0%"   stopColor="#60a5fa"/>
        <stop offset="100%" stopColor="#1e3a8a"/>
      </radialGradient>
      <radialGradient id="zsh-s1g" cx="50%" cy="50%" r="55%">
        <stop offset="0%"   stopColor="#93c5fd" stopOpacity="0.5"/>
        <stop offset="100%" stopColor="#1e40af" stopOpacity="0"/>
      </radialGradient>
    </defs>
    <path d="M26 2 L48 11 L48 28 C48 42 37 54 26 58 C15 54 4 42 4 28 L4 11 Z"
          fill="url(#zsh-s1)" stroke="#3b82f6" strokeWidth="1.5"/>
    <path d="M26 2 L48 11 L48 28 C48 42 37 54 26 58 C15 54 4 42 4 28 L4 11 Z"
          fill="url(#zsh-s1g)"/>
    <path d="M26 8 L43 15 L43 28 C43 39 35 49 26 53 C17 49 9 39 9 28 L9 15 Z"
          fill="none" stroke="#93c5fd" strokeWidth="0.5" strokeOpacity="0.35"/>
    <circle cx="18" cy="28" r="1.5" fill="#93c5fd" opacity="0.6"/>
    <circle cx="34" cy="20" r="1"   fill="#dbeafe" opacity="0.7"/>
    <circle cx="30" cy="33" r="1.2" fill="#60a5fa" opacity="0.5"/>
    <circle cx="20" cy="17" r="0.8" fill="#bfdbfe" opacity="0.8"/>
    <circle cx="37" cy="32" r="0.7" fill="#93c5fd" opacity="0.6"/>
    <circle cx="14" cy="22" r="0.6" fill="#dbeafe" opacity="0.7"/>
    <text x="26" y="33" textAnchor="middle" fill="#e0f2fe" fontSize="12"
          fontFamily="'Hiragino Mincho ProN','Yu Mincho','MS Mincho',serif">残心</text>
  </svg>
)

export const ShieldF2Icon = ({ width = 20, height = 23, className }: IconProps) => (
  <svg width={width} height={height} viewBox="0 0 52 60" fill="none"
       className={className} role="img" aria-label="protezione F2 attiva">
    <defs>
      <radialGradient id="zsh-s2" cx="38%" cy="25%" r="70%">
        <stop offset="0%"   stopColor="#c084fc"/>
        <stop offset="60%"  stopColor="#7c3aed"/>
        <stop offset="100%" stopColor="#2e1065"/>
      </radialGradient>
      <radialGradient id="zsh-s2n" cx="50%" cy="40%" r="60%">
        <stop offset="0%"   stopColor="#e9d5ff" stopOpacity="0.4"/>
        <stop offset="100%" stopColor="#4c1d95" stopOpacity="0"/>
      </radialGradient>
    </defs>
    <path d="M26 2 L48 11 L48 28 C48 42 37 54 26 58 C15 54 4 42 4 28 L4 11 Z"
          fill="url(#zsh-s2)" stroke="#a855f7" strokeWidth="1.5"/>
    <path d="M26 2 L48 11 L48 28 C48 42 37 54 26 58 C15 54 4 42 4 28 L4 11 Z"
          fill="url(#zsh-s2n)"/>
    <path d="M26 8 L43 15 L43 28 C43 39 35 49 26 53 C17 49 9 39 9 28 L9 15 Z"
          fill="none" stroke="#d8b4fe" strokeWidth="0.5" strokeOpacity="0.4"/>
    <path d="M20 22 L26 18 L32 22 L32 28 C32 33 29 36 26 38 C23 36 20 33 20 28 Z"
          fill="none" stroke="#c4b5fd" strokeWidth="0.5" strokeOpacity="0.35"/>
    <circle cx="15" cy="16" r="1.5" fill="#e9d5ff" opacity="0.9"/>
    <circle cx="37" cy="13" r="1"   fill="#f5f3ff" opacity="0.85"/>
    <circle cx="11" cy="26" r="1.2" fill="#c4b5fd" opacity="0.8"/>
    <circle cx="40" cy="24" r="0.9" fill="#ddd6fe" opacity="0.9"/>
    <circle cx="18" cy="34" r="1"   fill="#a78bfa" opacity="0.7"/>
    <circle cx="35" cy="33" r="1.3" fill="#c4b5fd" opacity="0.75"/>
    <circle cx="22" cy="42" r="0.8" fill="#e9d5ff" opacity="0.6"/>
    <circle cx="31" cy="43" r="0.7" fill="#d8b4fe" opacity="0.65"/>
    <circle cx="13" cy="20" r="0.6" fill="#f5f3ff" opacity="0.8"/>
    <circle cx="39" cy="18" r="0.5" fill="#f5f3ff" opacity="0.7"/>
    <circle cx="14" cy="32" r="0.5" fill="#c4b5fd" opacity="0.6"/>
    <circle cx="38" cy="36" r="0.6" fill="#ddd6fe" opacity="0.6"/>
    <circle cx="26" cy="44" r="0.7" fill="#e9d5ff" opacity="0.5"/>
    <circle cx="20" cy="14" r="0.5" fill="#f5f3ff" opacity="0.7"/>
    <circle cx="32" cy="15" r="0.4" fill="#ddd6fe" opacity="0.6"/>
    <path d="M25 10 L25.4 11.2 L26.7 11.2 L25.7 12 L26.1 13.2 L25 12.4 L23.9 13.2 L24.3 12 L23.3 11.2 L24.6 11.2 Z"
          fill="#f5f3ff" opacity="0.85"/>
    <path d="M38 28 L38.3 28.9 L39.2 28.9 L38.5 29.5 L38.7 30.4 L38 29.8 L37.3 30.4 L37.5 29.5 L36.8 28.9 L37.7 28.9 Z"
          fill="#ddd6fe" opacity="0.7"/>
    <path d="M13 35 L13.2 35.7 L13.9 35.7 L13.4 36.1 L13.6 36.8 L13 36.4 L12.4 36.8 L12.6 36.1 L12.1 35.7 L12.8 35.7 Z"
          fill="#c4b5fd" opacity="0.65"/>
    <text x="26" y="33" textAnchor="middle" fill="#f5f3ff" fontSize="12"
          fontFamily="'Hiragino Mincho ProN','Yu Mincho','MS Mincho',serif">残心</text>
  </svg>
)
